import { h } from 'preact';
import Image from 'utils/image';
import IconStar from 'utils/icon/icon-star.svg';
import { formatMoney } from 'utils/storefront/format-resource';
import { useEffect, useRef, useState } from 'preact/hooks';
import useResponsive from 'utils/responsive';
import ProductForm from '../product/product-form';
import ProductFormGift from '../product-gift-card/product-gift-card-form';
import extractAndFormat from './extract-and-format';

const ProductCard = ({ product, useProductGiftForm }) => {
  const url = `/products/${product.handle}`;
  const formRef = useRef(null);
  const variantSelectorsRef = useRef(null);
  const [isVisibleQuickAdd, setisVisibleQuickAdd] = useState(false);
  const { isMobile } = useResponsive();
  const wrapperRef = useRef(null);
  const kitSet = product.tags?.includes('kit_set');
  const [currentVariant, setCurrentVariant] = useState(
    product.variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) ===
        JSON.stringify(product.variantBySelectedOptions.selectedOptions),
    ),
  );
  const getBadge = () => {
    const badgeTag = product.tags.find((tag) => tag.includes('badge:'));

    if (badgeTag) {
      const badgeValue = badgeTag.split('badge:')[1];
      return (
        <div className="flex gap-1.5 absolute top-0 left-0 z-10">
          <div className="bg-supergoop-yellow py-1 px-[6px] body-xs rounded-[2px] z-10 pointer-events-none text-[10px] md:text-[11px] leading-[13px] md:leading-none font-medium text-supergoop-blue">
            {badgeValue}
          </div>
        </div>
      );
    }
    return '';
  };

  const handleHoverEnter = () => {
    if (!isMobile) {
      setisVisibleQuickAdd(true);
    }
  };
  const handleHoverLeave = () => {
    if (!isMobile) {
      setisVisibleQuickAdd(false);
    }
  };

  useEffect(() => {
    const wrapper = formRef.current.parentElement;
    const selector = variantSelectorsRef.current;
    if (isVisibleQuickAdd) {
      wrapper.style.maxHeight = `${selector.offsetHeight + 60}px`;
      variantSelectorsRef.current.style.opacity = 1;
      wrapper.classList.add(
        '[&_button]:text-supergoop-blue',
        '[&_button]:border-supergoop-blue',
      );
      wrapper.classList.remove('[&_button]:hidden');
    } else if (product.variants.length > 1) {
      wrapper.classList.add('[&_button]:hidden');
      wrapper.style.maxHeight = `0px`;
      variantSelectorsRef.current.style.opacity = 0;
    }
  }, [isVisibleQuickAdd]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setisVisibleQuickAdd(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <article
      className="group relative group flex flex-col lg:[&_form]:hover:block justify-between"
      data-product="article"
      ref={wrapperRef}
      onMouseEnter={handleHoverEnter}
      onMouseLeave={handleHoverLeave}
      tabIndex="-1"
    >
      <div>
        {getBadge()}

        <a className="relative block no-underline mb-3" href={url}>
          {product.images[0] && (
            <span
              className={`
                ${product.secondMedia
                  ? 'opacity:100 group-hover:opacity-0 transition-opacity duration-500'
                  : ''
                }`}
            >
              <Image {...product.images[0]} srcWidths={{ sm: 640 }} />
            </span>
          )}
          {!!currentVariant.hoverImage && (
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-2">
              <Image {...currentVariant.hoverImage} srcWidths={{ sm: 640 }} />
            </span>
          )}
          {!!product.secondMedia && !currentVariant.hoverImage && (
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-1">
              <Image {...product.secondMedia} srcWidths={{ sm: 640 }} />
            </span>
          )}
        </a>

        <div className="relative">
          <h3 className="normal-case leading-[0px] lg:pb-1.5">
            <a
              className="lg:link-list font-sans font-normal text-black no-underline body-xs"
              href={url}
            >
              {product.title}
            </a>
          </h3>
        </div>

        {!isVisibleQuickAdd && (
          <div>
            <div className="lg:pb-1.5 pb-0.5 leading-5">
              <span className="utility-s text-[10px] lg:text-[14px]">
                {extractAndFormat(
                  formatMoney(product.priceRange.minVariantPrice),
                )}

                {parseFloat(product.priceRange.maxVariantPrice.amount) >
                  parseFloat(product.priceRange.minVariantPrice.amount) &&
                  ` — ${extractAndFormat(
                    formatMoney(product.priceRange.maxVariantPrice),
                  )}`}
              </span>
              {parseFloat(product.compareAtPriceRange.minVariantPrice.amount) >
                0 &&
                !kitSet && (
                  <del className="ml-1 utility-s text-[10px] lg:text-[14px]">
                    {extractAndFormat(
                      formatMoney(product.compareAtPriceRange.minVariantPrice),
                    )}
                  </del>
                )}
              {parseFloat(product.compareAtPriceRange.minVariantPrice.amount) >
                0 &&
                kitSet && (
                  <a
                    href={url}
                    className="link-list ml-1 text-supergoop-blue text-[10px] lg:text-[14px]"
                  >
                    {extractAndFormat(
                      formatMoney(product.compareAtPriceRange.minVariantPrice),
                    )}{' '}
                    Value
                  </a>
                )}
            </div>

            <div className="pb-1.5 flex">
              <span className="text-supergoop-blue">
                <IconStar />
              </span>
              <span className="text-supergoop-blue">
                <IconStar />
              </span>
              <span className="text-supergoop-blue">
                <IconStar />
              </span>
              <span className="text-supergoop-blue">
                <IconStar />
              </span>
              <span className="text-supergoop-blue">
                <IconStar />
              </span>
            </div>

            <div className="flex flex-wrap mb-2.5">
              {product.bulletPoints && product.bulletPoints.slice(0, 3).map((item) => (
                <span
                  key={item.title}
                  className="text-grey-5C utility-xs flex items-center after:w-1 after:h-1 after:content-[''] after:block after:bg-sky-blue after:rounded after:mx-1.5 last:after:hidden"
                >
                  {item.title}
                </span>
              ))}

              {product.options.map(
                (option, index) =>
                  option.values.length > 1 && (
                    <span
                      key={index}
                      className="text-grey-5C utility-xs flex items-center after:w-1 after:h-1 after:content-[''] after:block after:bg-sky-blue after:rounded after:mx-1.5 last:after:hidden"
                    >
                      <span className="mr-0.5">{option.values.length} </span>
                      {option.name}s
                    </span>
                  ),
              )}
            </div>
          </div>
        )}
      </div>
      {!product.hasOnlyDefaultVariant &&
        product.variants.length > 1 &&
        !isVisibleQuickAdd && (
          <button
            className="cursor-pointer h-10 items-center py-3 px-3 label-10 uppercase flex justify-center w-full border border-grey-2A text-grey-2A lg:border-supergoop-blue lg:text-supergoop-blue mt-auto md:hidden"
            data-product="show-options"
            onClick={() => setisVisibleQuickAdd(true)}
          >
            {product.selectButton} {product.options[0].name}
          </button>
        )}
      <div
        className={`overflow-hidden ${product.variants.length > 1 &&
          'max-h-0 [&_form>div]:overflow-hidden [&_form>div]:opacity-0 [&_button]:hidden'
          } [&_form>div]:ease-in-out [&_form>div]:duration-1000`}
      >
        <div ref={formRef}>
          {useProductGiftForm ? <ProductFormGift
            product={product}
            isQuickView={true}
            currentVariant={currentVariant}
            setCurrentVariant={setCurrentVariant}
            kitSet={kitSet}
            url={url}
            ref={variantSelectorsRef}
          /> : <ProductForm
            product={product}
            isQuickView={true}
            currentVariant={currentVariant}
            setCurrentVariant={setCurrentVariant}
            kitSet={kitSet}
            url={url}
            ref={variantSelectorsRef}
          />}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
